/**
 * @fileoverview Login page component that handles user authentication with email/password,
 * SSO integration, and MFA verification. Provides registration and password recovery links.
 */

import { useState, useEffect, useContext, useRef } from "react";
import {
    Button,
    Card,
    CardContent,
    Container,
    Box,
    Typography,
    TextField,
    InputAdornment,
    Divider,
    Alert,
    Link,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import BusinessIcon from "@mui/icons-material/Business";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { APP_IMAGES } from "../../constants/AppImages";
import { ROUTES, LocalStorageVariables, intl, fetchData, HTTP_STATUS, LanguageBaseContext } from "dmsbasereact";
import { APP_COLORS } from "../../theme/appTheme";
import { getBaseURL } from "../../build_config";
import { LegalDocumentDialog } from "../../components/LegalDocumentDialog/LegalDocumentDialog";
import { NotificationsLena } from "../../utils/notificationsLena";

const PLATFORM_UPDATE_NOTICE_EXPIRES_AT = "2026-06-01";
const PLATFORM_UPDATE_NOTICE_FALLBACK_PT_BR = "O Programa Lena foi atualizado, e desde o dia 18/05/2026 está operando com novas funcionalidades. Se você já tinha acesso ao Programa Lena antes dessa data, orientamos que clique em <a>esqueceu sua senha</a> para redefinição da senha e novo acesso.";

const buildPlatformUpdateNoticeContent = (navigate) => {
    let raw;
    try {
        raw = intl.get("LoginPlatformUpdateNotice").d(PLATFORM_UPDATE_NOTICE_FALLBACK_PT_BR);
    } catch (e) {
        raw = PLATFORM_UPDATE_NOTICE_FALLBACK_PT_BR;
    }
    if (!raw) raw = PLATFORM_UPDATE_NOTICE_FALLBACK_PT_BR;

    const openTag = "<a>";
    const closeTag = "</a>";
    const startIdx = raw.indexOf(openTag);
    const endIdx = raw.indexOf(closeTag);

    if (startIdx < 0 || endIdx < 0 || endIdx < startIdx) {
        return <span>{raw}</span>;
    }

    const before = raw.slice(0, startIdx);
    const linkText = raw.slice(startIdx + openTag.length, endIdx);
    const after = raw.slice(endIdx + closeTag.length);

    return (
        <span>
            {before}
            <Link
                component="button"
                type="button"
                onClick={() => navigate(APP_ROUTES.ForgotPassword)}
                sx={{
                    color: "inherit",
                    textDecoration: "underline",
                    fontWeight: 700,
                    p: 0,
                    verticalAlign: "baseline",
                }}
            >
                {linkText}
            </Link>
            {after}
        </span>
    );
};

/**
 * Authenticates a user with the backend API
 * @param {Object} credentials - Login credentials
 * @param {string} credentials.login - User email or username
 * @param {string} credentials.password - User password
 * @returns {Promise<Object>} Response with data and status
 */
const loginUser = async ({ login, password }) => {
    const authentication_url = `${getBaseURL()}/auth/login`;

    const credentials = {
        grant_type: "password",
        username: login,
        password: password,
    };

    const body = new URLSearchParams(credentials).toString();

    try {
        const response = await fetch(authentication_url, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/x-www-form-urlencoded",
            },
            body: body,
        });

        const data = await response.json();

        return {
            data: data,
            status: response.status,
        };
    } catch (error) {
        return {
            data: { detail: intl.get("UnableToConnectServer").d("Unable to connect to server. Please try again later.") },
            status: 500,
        };
    }
};

/**
 * Login page component with email/password authentication, SSO option, and MFA support
 * @returns {JSX.Element} The login page with authentication form
 */
export const LoginPage = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [legalDocument, setLegalDocument] = useState(null);
    const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
    const [loadingDocument, setLoadingDocument] = useState(false);
    const { language } = useContext(LanguageBaseContext);
    const platformUpdateToastIdRef = useRef(null);

    // Fetch legal document on mount
    useEffect(() => {
        const fetchLegalDocument = async () => {
            try {
                const response = await fetchData("/legal-document/public/terms_of_service", 'GET', false);
                if (response.status === HTTP_STATUS.OK_200) {
                    setLegalDocument(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch legal document:", error);
            }
        };

        fetchLegalDocument();
    }, []);

    useEffect(() => {
        if (new Date() >= new Date(PLATFORM_UPDATE_NOTICE_EXPIRES_AT)) {
            return;
        }
        if (platformUpdateToastIdRef.current) {
            NotificationsLena.clearToast(platformUpdateToastIdRef.current);
        }
        platformUpdateToastIdRef.current = NotificationsLena.toast(
            buildPlatformUpdateNoticeContent(navigate),
            NotificationsLena.TYPE_WARNING,
            false,
            0
        );
    }, [language, navigate]);

    const handleOpenDocument = () => {
        setDocumentDialogOpen(true);
    };

    const handleCloseDocument = () => {
        setDocumentDialogOpen(false);
    };

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!email || !password) {
            setError(intl.get("PleaseFillAllFields").d("Please fill in all fields"));
            return;
        }

        setLoading(true);

        try {
            const user_auth = await loginUser({
                login: email,
                password: password,
            });

            setLoading(false);

            if (user_auth.status === 200) {
                // Check if MFA is required
                if (user_auth.data.mfa_required) {
                    // Navigate to MFA verification page
                    navigate('/login/mfa', {
                        state: {
                            pendingToken: user_auth.data.pending_token,
                            mfaMethods: user_auth.data.mfa_methods,
                            emailMasked: user_auth.data.email_masked
                        }
                    });
                    return;
                }

                // Normal login flow (no MFA required)
                sessionStorage.setItem(LocalStorageVariables.PageToken, JSON.stringify(user_auth.data));
                sessionStorage.setItem("expirationSwalShow", "true");

                if (user_auth.data.refresh_token) {
                    const refreshToken = {
                        refreshToken: user_auth.data.refresh_token,
                        tokenUses: 0
                    };
                    sessionStorage.setItem("refresh", JSON.stringify(refreshToken));
                }

                if (user_auth.data.inactivity_timer) {
                    localStorage.setItem(
                        LocalStorageVariables.InactivityTimer,
                        user_auth.data.inactivity_timer
                    );
                }

                window.location.href = ROUTES.Home;
            } else {
                // Handle error response - detail can be a string or an object (e.g., rate limit response)
                let errorMessage;
                const detail = user_auth.data.detail;
                if (typeof detail === 'object' && detail !== null) {
                    // Rate limit or structured error response
                    errorMessage = detail.message || detail.error || intl.get("InvalidCredentials").d("Invalid credentials");
                } else {
                    errorMessage = detail || intl.get("InvalidCredentials").d("Invalid credentials");
                }
                setError(errorMessage);
            }
        } catch (error) {
            setLoading(false);
            console.error("Login error:", error);
            setError(intl.get("LoginErrorTryAgain").d("Error trying to login. Please try again."));
        }
    };

    return (
        <Container
            className="login-page"
            maxWidth="false"
            disableGutters
            sx={{
                background: '#FFFFFF',
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    maxWidth: "450px",
                    px: 2,
                }}
            >
                {/* Logo/Title Section */}
                <Box
                    sx={{
                        mb: 4,
                        textAlign: "center",
                    }}
                >
                    <Box
                        component="img"
                        src={APP_IMAGES.LOGO}
                        alt="Lena Logo"
                        sx={{
                            width: "180px",
                            height: "auto",
                            mb: 3,
                            filter: "drop-shadow(2px 2px 8px rgba(0,0,0,0.3))",
                        }}
                    />
                </Box>

                {/* Login Card */}
                <Card
                    sx={{
                        width: "100%",
                        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                        borderRadius: 3,
                    }}
                >
                    <CardContent sx={{ p: 4 }}>
                        <Typography
                            variant="h5"
                            sx={{
                                textAlign: "center",
                                mb: 3,
                                fontWeight: 600,
                                color: APP_COLORS.neutral.gray900,
                            }}
                        >
                            {intl.get("Welcome").d("Welcome")}
                        </Typography>

                        {/* Error Alert */}
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {/* Login Form */}
                        <form onSubmit={handleLoginSubmit} noValidate>
                            <Box mb={2}>
                                <TextField
                                    required
                                    fullWidth
                                    id="email"
                                    label={intl.get("EmailAddress").d("Email Address")}
                                    type="email"
                                    variant="outlined"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value.replace(/\s/g, ''))}
                                    disabled={loading}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <EmailIcon sx={{ color: APP_COLORS.neutral.gray600 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>

                            <Box mb={3}>
                                <TextField
                                    required
                                    fullWidth
                                    id="password"
                                    label={intl.get("Password").d("Password")}
                                    type="password"
                                    variant="outlined"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    disabled={loading}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <LockIcon sx={{ color: APP_COLORS.neutral.gray600 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={loading}
                                sx={{
                                    backgroundColor: APP_COLORS.primary.main,
                                    color: APP_COLORS.primary.contrastText,
                                    height: "50px",
                                    textTransform: "none",
                                    fontSize: "16px",
                                    fontWeight: "bold",
                                    borderRadius: 2,
                                    mb: 2,
                                    "&:hover": {
                                        backgroundColor: APP_COLORS.primary.dark,
                                    },
                                }}
                            >
                                {loading ? intl.get("SigningIn").d("Signing In...") : intl.get("SignIn").d("Sign In")}
                            </Button>

                            {/* Forgot Password Link */}
                            <Box sx={{ textAlign: "center", mb: 2 }}>
                                <Link
                                    component="button"
                                    type="button"
                                    variant="body2"
                                    onClick={() => navigate(APP_ROUTES.ForgotPassword)}
                                    sx={{
                                        color: APP_COLORS.primary.main,
                                        textDecoration: "none",
                                        "&:hover": {
                                            textDecoration: "underline",
                                        },
                                    }}
                                >
                                    {intl.get("ForgotPassword").d("Forgot your password?")}
                                </Link>
                            </Box>

                            {/* Divider with "OR" */}
                            <Divider sx={{ my: 3 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: APP_COLORS.text.secondary,
                                        px: 2,
                                        fontWeight: 500,
                                    }}
                                >
                                    {intl.get("Or").d("OR")}
                                </Typography>
                            </Divider>

                            {/* SSO Button - NOW VISIBLE FROM START */}
                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<BusinessIcon />}
                                onClick={() => navigate(APP_ROUTES.SsoLogin)}
                                disabled={loading}
                                sx={{
                                    height: "50px",
                                    textTransform: "none",
                                    fontSize: "16px",
                                    fontWeight: "bold",
                                    borderRadius: 2,
                                    borderColor: APP_COLORS.neutral[300],
                                    color: APP_COLORS.text.primary,
                                    mb: 2,
                                    "&:hover": {
                                        borderColor: APP_COLORS.primary.main,
                                        backgroundColor: "rgba(0, 122, 255, 0.04)",
                                    },
                                }}
                            >
                                {intl.get("SignInWithSSO").d("Sign in with SSO")}
                            </Button>

                            {/* Register Link */}
                            <Divider sx={{ my: 3 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: APP_COLORS.text.secondary,
                                        px: 2,
                                        fontWeight: 500,
                                    }}
                                >
                                    {intl.get("Or").d("OR")}
                                </Typography>
                            </Divider>

                            <Box sx={{ textAlign: "center" }}>
                                <Typography variant="body2" sx={{ color: APP_COLORS.neutral.gray600 }}>
                                    {intl.get("DontHaveAccount").d("Don't have an account?")}{" "}
                                    <Link
                                        component="button"
                                        type="button"
                                        variant="body2"
                                        onClick={() => navigate(APP_ROUTES.Register)}
                                        sx={{
                                            color: APP_COLORS.primary.main,
                                            fontWeight: 600,
                                            textDecoration: "none",
                                            "&:hover": {
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        {intl.get("CreateAccount").d("Create Account")}
                                    </Link>
                                </Typography>
                            </Box>
                        </form>
                    </CardContent>
                </Card>

                {/* Footer */}
                <Box sx={{ mt: 4, textAlign: "center" }}>
                    {/* Contact Information */}
                    <Typography
                        component="div"
                        variant="body2"
                        sx={{
                            color: APP_COLORS.neutral.gray600,
                            mb: 2,
                            px: 2,
                        }}
                    >
                        {intl.get("ContactInfoFooterPrefix").d("Em caso de dúvidas ou solicitações, entre em contato com o Programa Lena através do telefone")}{" "}
                        <Box component="span" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                            0800 703 0001
                        </Box>{" "}
                        {intl.get("ContactInfoFooterOr").d("ou do e‑mail")}{" "}
                        <Box component="span" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                            programalena@eurofarma.com
                        </Box>
                    </Typography>

                    {/* Legal Documents Link */}
                    <Box sx={{ textAlign: "center", mb: 2 }}>
                        <Link
                            component="button"
                            type="button"
                            variant="body2"
                            onClick={handleOpenDocument}
                            sx={{
                                color: APP_COLORS.primary.main,
                                textDecoration: "none",
                                "&:hover": {
                                    textDecoration: "underline",
                                },
                            }}
                        >
                            {intl.get("TermsOfUseAndPrivacyPolicy").d("Terms of Use and Privacy Policy")}
                        </Link>
                    </Box>

                    {/* Copyright */}
                    <Typography
                        variant="body2"
                        sx={{
                            color: APP_COLORS.neutral.gray500,
                        }}
                    >
                        {intl.get("CopyrightLena", { year: new Date().getFullYear() }).d("© {year} Lena. All rights reserved.")}
                    </Typography>
                </Box>
            </Box>

            {/* Legal Document Dialog */}
            <LegalDocumentDialog
                open={documentDialogOpen}
                onClose={handleCloseDocument}
                document={legalDocument}
                loading={loadingDocument}
            />
        </Container>
    );
};
